
import { defineAsyncComponent } from 'vue';

// Functional imports (needed for initialize function)
import { auth } from './views/store/auth.js';
import { users } from './views/store/users.js';
import otpPlugin from './views/plugins/otp.plugin.js';
import popupAuthPlugin, { showAuth, closeAuth, cancelAuth } from './views/plugins/popup.auth.plugin.js';

// Router
import addRoutes from '@martyrs/src/modules/core/views/router/addRoutes.js';
import { getAuthRoutes } from './views/router/auth.router.js';
import { getUsersRoutes, getProfileRoutes, getProfileSettingsRoutes } from './views/router/users.router.js';
import routerBackofficeUsers from './views/router/users.backoffice.router.js';

// Middlewares
import * as validationAuth from '@martyrs/src/modules/auth/views/middlewares/auth.validation.js';

// Локали модуля: язык грузит module.manager при активации, только текущий.
export const locales = {
  en: () => import('./locales/en.js'),
  ru: () => import('./locales/ru.js'),
};

// Component re-exports (enables tree shaking)
export const Auth = defineAsyncComponent(() => import('./views/components/layouts/Auth.vue'));
export const Invite = defineAsyncComponent(() => import('./views/components/pages/Invite.vue'));
export const Profile = defineAsyncComponent(() => import('./views/components/pages/Profile.vue'));
export const ProfileEdit = defineAsyncComponent(() => import('./views/components/pages/ProfileEdit.vue'));
export const ResetPassword = defineAsyncComponent(() => import('./views/components/pages/ResetPassword.vue'));
export const SignIn = defineAsyncComponent(() => import('./views/components/pages/SignIn.vue'));
export const SignUp = defineAsyncComponent(() => import('./views/components/pages/SignUp.vue'));
export const FeaturedUsers = defineAsyncComponent(() => import('./views/components/sections/FeaturedUsers.vue'));
export const SliderFeatures = defineAsyncComponent(() => import('./views/components/sections/SliderFeatures.vue'));

// Маршруты модуля объявляются на старте приложения (moduleManager.registerAll),
// поэтому роутер знает про /auth/* до любой навигации.
export function register(app, store, router, options = {}) {
  const authRoute = getAuthRoutes(options);
  addRoutes(router, { ...authRoute.config, parentName: authRoute.parentName });

  // Users client context:
  //   /users/:_id          — публичный read-only просмотр чужого
  //   /profile, /profile/* — свой профиль/настройки/дашборд (без ID в URL)
  if (!options.contexts || options.contexts.includes('client')) {
    const usersRoute = getUsersRoutes(options);
    addRoutes(router, { ...usersRoute.config, parentName: usersRoute.parentName });

    const profileRoute = getProfileRoutes(options);
    addRoutes(router, { ...profileRoute.config, parentName: profileRoute.parentName });
  }

  // Settings-маунт: настройки профиля вложенным роутом под роут проекта,
  // НЕЗАВИСИМО от contexts (проект может держать contexts: ['backoffice'],
  // не регистрируя публичные /users/:_id и /profile, но иметь настройки
  // внутри своего дашборда). Конфиг — плоские ключи в settings, в стиле
  // notifications (settings: { routeUsers: ... }):
  //   settings: { routeProfile: 'Dashboard', pathProfile: 'settings' }
  // module.manager прокидывает произвольный конфиг только внутри `settings`;
  // при ручном initialize() опции приходят плоско — поддерживаем оба пути.
  const settings = { ...options, ...(options.settings || {}) };
  if (settings.routeProfile) {
    const profileSettingsRoute = getProfileSettingsRoutes({
      route: settings.routeProfile,
      path: settings.pathProfile,
    });
    addRoutes(router, { ...profileSettingsRoute.config, parentName: profileSettingsRoute.parentName });
  }

  // Users backoffice context
  if (!options.contexts || options.contexts.includes('backoffice')) {
    addRoutes(router, {
      parentName: options.routeBackoffice || 'Backoffice Root',
      basePath: 'users',
      routes: routerBackofficeUsers,
      routeNamePrefix: 'Backoffice',
      meta: { context: 'backoffice' },
    });
  }
}

// Пример функции инициализации для модуля аутентификации
function initializeAuth(app, store, router, options = {}) {
  // Экземпляры сторов auth и users создаём здесь обращением к их state: личность
  // из куки кладёт core.app.js через store.auth (core не импортирует модуль
  // auth), а туда экземпляр попадает только при первом обращении к константе.
  void auth.state;
  void users.state;

  // OTP plugin для challenge-response
  app.use(otpPlugin);

  // Auth popup plugin
  app.use(popupAuthPlugin);
}

const ModuleAuth = {
  register,
  initialize: initializeAuth,
  views: {
    store: {
      auth,
      users,
    },
    router: {
      getAuthRoutes,
      getUsersRoutes,
    },
    middlewares: {
      validationAuth,
    },
  },
};

// Functional exports
export {
  auth,
  users,
  getAuthRoutes,
  getUsersRoutes,
  validationAuth,
  initializeAuth as initialize,
  // Auth popup
  showAuth,
  closeAuth,
  cancelAuth,
};

export default ModuleAuth;
